import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	OptimizationIDModel,
	ProductsByEngineModel,
	StatisticsInterpretabilityGroupedResponseModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'

import { any, getOr, map, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useProductCountByEngineStatisticsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'

export default function NumericRevenueImpactWidget({
	commonFilters,
	className,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const optimizationsQuery = useOptimizationsQuery({
		select: getFinishedAndLockedOptimizations,
	})
	const productCountQuery = useProductCountByEngineStatisticsQuery(
		{
			currency,
			filters: [
				...commonFilters,
				{
					name: 'optimization_group_id',
					operation: Operators.IN,
					value: map(
						prop('optimization_group_id'),
						optimizationsQuery.data ?? []
					),
				},
			],
		},
		{
			enabled: !!optimizationsQuery.data,
			select: computeProductsCount,
		}
	)
	const revenueQuery = useInterpretabilityStatisticQuery(
		{
			currency,
			optimizations: optimizationsQuery.data as OptimizationIDModel[],
			filters: commonFilters,
		},
		{
			enabled: !!optimizationsQuery.data,
			select: calculateRevenueStatistics,
		}
	)
	const isLoading = any(prop('isLoading'), [
		revenueQuery,
		productCountQuery,
		optimizationsQuery,
	])
	const isError = any(prop('isError'), [
		revenueQuery,
		productCountQuery,
		optimizationsQuery,
	])

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.revenue_impact.title', {
					defaultValue: 'Revenue Impact',
				})}
				tooltip={t('widget.revenue_impact.tooltip', {
					defaultValue:
						'Projected revenue over the next 7 days based on recommended prices. The % delta shows the uplift versus keeping current prices unchanged.',
				})}
				subtitle={t('widget.revenue_impact.subtitle', {
					defaultValue: 'Projected Revenue Uplift',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				<div className='mt-auto w-full'>
					{isLoading && <Loader />}
					{!isLoading && (
						<div className='space-y-1'>
							<p className='text-xs text-muted-foreground'>
								{formatNumber(productCountQuery?.data ?? 0)}{' '}
								{t('general_skus', { defaultValue: 'SKUs' })}
							</p>
							<div className='space-y-1'>
								<PercentDifferenceBadge
									value={revenueQuery?.data?.revenueDeltaPercent ?? 0}
								/>
								<p className='truncate text-3xl font-semibold'>
									{formatMoney(
										revenueQuery?.data?.revenue ?? 0,
										currency,
										NumberFormats.Compact
									)}
								</p>
							</div>
						</div>
					)}
				</div>
			</WidgetContent>
		</Widget>
	)
}
const Loader = () => (
	<div className='space-y-1'>
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-8 w-32' />
	</div>
)

const calculateRevenueStatistics = (
	data:
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null
) => {
	if (!data) return { revenue: 0, revenueDeltaPercent: 0 }
	const finalRevenue = getOr(0, ['metrics', 'predict_revenue.final'], data)
	const initialRevenue = getOr(0, ['metrics', 'predict_revenue.init'], data)
	const revenueDeltaPercent =
		((finalRevenue - initialRevenue) / initialRevenue) * 100

	return {
		revenue: finalRevenue,
		revenueDeltaPercent,
	}
}
const computeProductsCount = (data: ProductsByEngineModel[] | null) =>
	data
		?.filter(
			({ pricing_campaign_engine }) => pricing_campaign_engine !== 'locked'
		)
		.map(({ product_count }) => product_count ?? 0)
		.reduce((acc, curr) => acc + curr, 0)
